import { useTranslation } from "react-i18next";
import type { TimeAttendanceStepProps } from "@/types/timeAttendance";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../ui/button";

const TimeAttendanceRuleCapturePhotoSteps: React.FC<
  TimeAttendanceStepProps
> = ({ onNext, onReject, capturedPhoto, setCapturedPhoto }) => {
  const { t } = useTranslation(["common", "my-space"]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const hasValidPhoto =
    typeof capturedPhoto === "string" &&
    capturedPhoto.startsWith("data:image") &&
    capturedPhoto.length > 100;

  /* ================= STOP CAMERA ================= */

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // VERY IMPORTANT → prevents memory leak
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  /* ================= START CAMERA ================= */

  const startCamera = useCallback(async () => {
    // Prevent duplicate streams
    if (streamRef.current) return;

    try {
      setIsLoading(true);
      setError("");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setIsLoading(false);
    } catch (err: any) {
      console.error(err);

      if (err?.name === "NotAllowedError") {
        setError(t("my-space:checkin.camera_permission_denied"));
      } else if (err?.name === "NotFoundError") {
        setError(t("my-space:checkin.no_camera_device_found"));
      } else {
        setError(t("my-space:checkin.unable_to_access_camera"));
      }

      setIsLoading(false);
    }
  }, [t]);

  /* ================= MOUNT / UNMOUNT ================= */

  useEffect(() => {
    if (!navigator.mediaDevices) {
      setError(t("my-space:checkin.camera_not_supported"));
      setIsLoading(false);
      return;
    }
    if (!capturedPhoto) {
      startCamera();
    }
    return stopCamera;
  }, [capturedPhoto, startCamera, stopCamera]);

  /* ================= STOP WHEN TAB HIDDEN ================= */

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) stopCamera();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [stopCamera]);

  /* ================= CAPTURE ================= */

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvasRef.current.toDataURL("image/jpeg", 0.9);
    setCapturedPhoto?.(dataUrl);
    stopCamera();
  };

  /* ================= RETAKE ================= */

  const handleRetake = () => {
    stopCamera();
    setCapturedPhoto?.(null);
    setTimeout(startCamera, 250);
  };

  /* ================= NAVIGATION ================= */

  const handleUsePhoto = () => {
    stopCamera();
    onNext?.();
  };

  const handleCancel = () => {
    stopCamera();
    onReject?.("Camera access denied");
  };

  const hasStream = !!streamRef.current;
  const isDenied = !!error;

  return (
    <div className="flex flex-col items-center gap-2 py-6 max-w-md mx-auto">
      {/* ================= PHOTO PREVIEW ================= */}

      {capturedPhoto ? (
        <>
          <img
            src={capturedPhoto}
            alt={t("my-space:checkin.captured")}
            className="w-full h-64 object-cover rounded-md"
          />

          <div className="flex w-full gap-2 mt-4">
            <button
              onClick={handleRetake}
              className="flex-1 py-2 border rounded-xs hover:bg-gray-50"
            >
              {t("my-space:checkin.retake")}
            </button>

            <Button
              disabled={!hasValidPhoto}
              onClick={handleUsePhoto}
              className="flex-1 py-2 bg-indigo-600 text-white rounded-xs hover:bg-indigo-700"
            >
              {t("my-space:checkin.use_photo")}
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* ================= CAMERA / ERROR ================= */}

          <div className="w-full relative">
            {isDenied ? (
              <div className="w-full h-64 bg-border rounded-md flex items-center justify-center">
                <AlertCircle size={48} className="text-red-400" />
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-64 bg-gray-800 rounded-md"
                />

                {isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 rounded-md">
                    <Loader2 className="animate-spin w-10 h-10 text-white mb-2" />
                    <span className="text-white text-xs">
                      {t("my-space:checkin.starting_camera")}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {isDenied ? (
            <>
              <h3 className="text-lg font-semibold mt-6">
                {t("my-space:checkin.camera_access_denied")}
              </h3>

              <p className="text-sm text-gray-500 text-center mb-4 max-w-xs">
                {error}
              </p>

              <button
                onClick={startCamera}
                className="w-full h-11 bg-indigo-600 text-white rounded-xs mb-2 hover:bg-indigo-700"
              >
                {t("my-space:checkin.retry_camera_access")}
              </button>

              <button
                onClick={handleCancel}
                className="w-full h-11 border bg-gray-50 rounded-xs hover:bg-gray-100"
              >
                {t("common:buttons.cancel")}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 text-center">
                {t("my-space:checkin.take_clear_photo")}
              </p>

              <div className="w-14 h-14 rounded-full p-1 flex items-center justify-center border-4 border-border">
                <button
                  onClick={handleCapture}
                  disabled={!hasStream || isLoading}
                  className="rounded-full w-full h-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </div>
            </>
          )}
        </>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default TimeAttendanceRuleCapturePhotoSteps;
